// src/utils/taskAssignment.js
// --------------------------------------------------------------------------
// Helpers para asignar una tarea/alerta a un USUARIO REAL del sistema y para
// construir el correo de notificación.
//
// Antes el responsable era un literal fijo ("Producción" / "Diseño"). Ahora la
// asignación apunta a un documento real de la colección `users` (ligado a
// Firebase Auth por su UID).
// --------------------------------------------------------------------------

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Nombre legible de un usuario (los docs pueden traer displayName, name o solo email).
export const userLabel = (u) =>
    (u && (u.displayName || u.name || u.email)) || 'Sin nombre';

// Campos de asignación que se guardan en el documento de la tarea.
// `assignedToName` se mantiene por compatibilidad con la UI existente.
export const buildTaskAssignment = (user) => {
    if (!user) return {};
    return {
        assignedTo: user.id || null,
        assignedToUid: user.id || null,
        assignedToEmail: user.email || null,
        assignedToName: userLabel(user),
    };
};

// URL pública de la app (Firebase Hosting). El link del correo abre directo la
// pestaña "Tareas" del trabajo correspondiente.
export const APP_BASE_URL = 'https://project-master-b8fe9.web.app';

// Asignación a VARIOS usuarios (checklist). Mantiene también los campos singulares
// por compatibilidad y para que el trigger logTaskActivity detecte la asignación.
export const buildMultiAssignment = (usersArr) => {
    const list = (usersArr || []).filter(Boolean);
    const names = list.map(userLabel);
    return {
        assignedToUids: list.map(u => u.id),
        assignedToEmails: list.map(u => u.email).filter(Boolean),
        assignedToNames: names,
        assignedToName: names.join(', '),   // texto legible (compat con UI existente)
        assignedTo: list[0]?.id || null,     // compat
        assignedToUid: list[0]?.id || null,  // dispara el log de asignación
    };
};

// Sello del ACTOR que realiza la asignación (quién asigna), para auditoría y correo.
// Se mezcla en el documento de la tarea al asignar/reasignar, de modo que el dato
// no se pierda aunque la tarea ya existiera (createdBy solo refleja al creador).
export const buildAssignedByStamp = (actor) => {
    if (!actor) return {};
    return {
        assignedByUid: actor.uid || actor.id || null,
        assignedByName: userLabel(actor),
        assignedByEmail: actor.email || null,
    };
};

// Construye el documento para la colección `mail/` que consume la función de
// correo. UN solo correo por alerta/asignación.
//   to:         destinatario (correo del usuario asignado)
//   assignedBy: usuario (sesión) que realiza la asignación → se muestra "Asignada por"
//   message:    { subject, html, text }
export const buildAssignmentEmail = ({ assignedUser, assignedBy, taskTitle, taskDescription, jobId, suggestedAction }) => {
    if (!assignedUser || !assignedUser.email) return null;

    const safeDesc = taskDescription || 'Sin descripción.';
    const assignerName = assignedBy ? userLabel(assignedBy) : null;
    const taskLink = `${APP_BASE_URL}/jobs/${encodeURIComponent(jobId)}?tab=checklist`;
    const actionBlock = suggestedAction
        ? `<p style="margin:12px 0;padding:10px;background:#f3f4f6;border-radius:6px;">
               <strong>Acción sugerida (IA):</strong><br/>${suggestedAction}
           </p>`
        : '';
    const assignerHtml = assignerName
        ? `<p style="margin:4px 0 12px;font-size:13px;color:#444;">Asignada por: <strong>${assignerName}</strong></p>`
        : '';

    return {
        to: [assignedUser.email],
        message: {
            subject: `Nueva tarea asignada: ${taskTitle}`,
            text: `Hola ${userLabel(assignedUser)},\n\n` +
                (assignerName
                    ? `${assignerName} te ha asignado una tarea en el trabajo ${jobId}.\n\n`
                    : `Se te ha asignado una tarea en el trabajo ${jobId}.\n\n`) +
                `${taskTitle}\n${safeDesc}\n\n` +
                (suggestedAction ? `Acción sugerida (IA): ${suggestedAction}\n\n` : '') +
                `Abre la tarea aquí: ${taskLink}`,
            html: `<div style="font-family:sans-serif;color:#111;">
                       <p>Hola <strong>${userLabel(assignedUser)}</strong>,</p>
                       <p>${assignerName ? `<strong>${assignerName}</strong> te ha asignado` : 'Se te ha asignado'} una tarea en el trabajo <strong>${jobId}</strong>:</p>
                       <h3 style="margin:8px 0;">${taskTitle}</h3>
                       ${assignerHtml}
                       <p>${safeDesc}</p>
                       ${actionBlock}
                       <p style="margin:20px 0;">
                           <a href="${taskLink}" style="background:#2563eb;color:#fff;text-decoration:none;padding:10px 18px;border-radius:6px;font-weight:bold;display:inline-block;">
                               Abrir tarea en CARTONBAR
                           </a>
                       </p>
                       <p style="font-size:12px;color:#666;">Si el botón no funciona, copia este enlace: ${taskLink}</p>
                   </div>`,
        },
        // Metadatos útiles para depurar / auditar.
        _meta: { jobId, taskTitle, taskLink, recipientUid: assignedUser.id || null, assignedByUid: (assignedBy && (assignedBy.uid || assignedBy.id)) || null },
    };
};

// Llama a la Cloud Function que limpia el texto del problema y sugiere una acción.
// Devuelve { cleanedDescription, suggestedAction } o null si falla.
export const requestAiSuggestion = async ({ problem, sheetName, sheetContext }) => {
    try {
        const functions = getFunctions(getApp(), 'us-central1');
        const polish = httpsCallable(functions, 'polishTaskAndSuggest');
        const result = await polish({ problem, sheetName, sheetContext });
        return result.data || null;
    } catch (error) {
        console.error('Error solicitando sugerencia de IA:', error);
        return null;
    }
};
